import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Users, Instagram } from 'lucide-react';
import { API, useAuthHeader } from './hooks';
import Modal from './Modal';
import ImageUploadField from './ImageUploadField';

const emptyForm = {
  name: '',
  specialization: '',
  bio: '',
  image_url: '',
  instagram_handle: '',
  portfolio_images: [],
};

const ArtistsManager = () => {
  const getAuthHeader = useAuthHeader();
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState('create');
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [newPortfolioUrl, setNewPortfolioUrl] = useState('');

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API}/artists`);
      setItems(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const openCreate = () => {
    setMode('create');
    setForm(emptyForm);
    setCurrent(null);
    setNewPortfolioUrl('');
    setShowModal(true);
  };

  const openEdit = (item) => {
    setMode('edit');
    setCurrent(item);
    setForm({
      name: item.name || '',
      specialization: item.specialization || '',
      bio: item.bio || '',
      image_url: item.image_url || '',
      instagram_handle: item.instagram_handle || '',
      portfolio_images: Array.isArray(item.portfolio_images) ? item.portfolio_images : [],
    });
    setNewPortfolioUrl('');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this artist? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/artists/${id}`, getAuthHeader());
      toast.success('Artist deleted');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const addPortfolioImage = (url) => {
    if (!url) return;
    setForm((p) => ({ ...p, portfolio_images: [...p.portfolio_images, url] }));
    setNewPortfolioUrl('');
  };

  const removePortfolioImage = (idx) => {
    setForm((p) => ({
      ...p,
      portfolio_images: p.portfolio_images.filter((_, i) => i !== idx),
    }));
  };

  const handleSubmit = async () => {
    try {
      if (mode === 'create') {
        await axios.post(`${API}/artists`, form, getAuthHeader());
        toast.success('Artist added');
      } else {
        await axios.put(`${API}/artists/${current.id}`, form, getAuthHeader());
        toast.success('Artist updated');
      }
      setShowModal(false);
      fetchItems();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  return (
    <div data-testid="artists-content">
      <div className="flex justify-between items-center mb-8 flex-wrap gap-3">
        <h2 className="font-heading text-3xl flex items-center gap-3">
          <Users size={28} /> Artists
        </h2>
        <button
          onClick={openCreate}
          data-testid="add-artist-btn"
          className="flex items-center gap-2 bg-black text-white px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-gray-800"
        >
          <Plus size={16} /> Add Artist
        </button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-200">
          <p className="font-body text-gray-500">No artists yet. Click &ldquo;Add Artist&rdquo; to add your first team member.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {items.map((artist) => (
            <div
              key={artist.id}
              data-testid={`artist-item-${artist.id}`}
              className="bg-white border border-gray-200 overflow-hidden group"
            >
              <div className="flex gap-4 p-4">
                <div className="w-32 h-32 flex-shrink-0 bg-gray-100 overflow-hidden">
                  {artist.image_url ? (
                    <img src={artist.image_url} alt={artist.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Users size={32} className="text-gray-300" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading text-xl font-bold truncate">{artist.name}</h3>
                  {artist.specialization && (
                    <p className="font-mono text-[10px] uppercase text-gray-500 mt-1">{artist.specialization}</p>
                  )}
                  {artist.bio && (
                    <p className="font-body text-sm text-gray-600 mt-2 line-clamp-3 whitespace-pre-line">{artist.bio}</p>
                  )}
                  <div className="flex items-center gap-3 mt-3">
                    {artist.instagram_handle && (
                      <span className="inline-flex items-center gap-1 font-mono text-[10px] text-gray-500">
                        <Instagram size={12} /> @{artist.instagram_handle}
                      </span>
                    )}
                    {artist.portfolio_images?.length > 0 && (
                      <span className="font-mono text-[10px] uppercase bg-gray-100 px-2 py-0.5">
                        {artist.portfolio_images.length} portfolio images
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="px-4 py-2 border-t border-gray-100 flex gap-2 justify-end">
                <button
                  onClick={() => openEdit(artist)}
                  data-testid={`edit-artist-${artist.id}`}
                  className="flex items-center gap-1 bg-gray-100 px-3 py-1 font-mono text-xs uppercase hover:bg-gray-200"
                >
                  <Edit size={14} /> Edit
                </button>
                <button
                  onClick={() => handleDelete(artist.id)}
                  data-testid={`delete-artist-${artist.id}`}
                  className="flex items-center gap-1 bg-red-100 text-red-700 px-3 py-1 font-mono text-xs uppercase hover:bg-red-200"
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={`${mode === 'create' ? 'Add' : 'Edit'} Artist`}
        footer={
          <button
            onClick={handleSubmit}
            disabled={!form.name}
            data-testid="artist-form-submit"
            className="w-full bg-black text-white py-4 font-bold text-sm uppercase tracking-wider hover:bg-gray-800 disabled:opacity-50"
          >
            {mode === 'create' ? 'Add Artist' : 'Save Changes'}
          </button>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="artist-form-name"
              className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
              placeholder="e.g. Apeksha"
            />
          </div>

          <div>
            <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Specialization / Designation</label>
            <input
              type="text"
              value={form.specialization}
              onChange={(e) => setForm({ ...form, specialization: e.target.value })}
              data-testid="artist-form-specialization"
              className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
              placeholder="e.g. Founder & Master Artist"
            />
          </div>

          <ImageUploadField
            value={form.image_url}
            onChange={(url) => setForm({ ...form, image_url: url })}
            label="Profile Photo"
            testId="artist-image"
          />

          <div>
            <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Bio</label>
            <textarea
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              rows={8}
              data-testid="artist-form-bio"
              className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none resize-none"
              placeholder="Full write-up shown on the About page. Line breaks preserved."
            />
          </div>

          <div>
            <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Instagram Handle</label>
            <div className="flex items-center border-2 border-gray-300 focus-within:border-black">
              <span className="pl-4 pr-2 font-mono text-gray-400">@</span>
              <input
                type="text"
                value={form.instagram_handle}
                onChange={(e) => setForm({ ...form, instagram_handle: e.target.value.replace(/^@/, '') })}
                data-testid="artist-form-instagram"
                className="flex-1 px-2 py-3 focus:outline-none"
                placeholder="vision.ttt"
              />
            </div>
          </div>

          <div>
            <label className="font-mono text-xs uppercase tracking-wider mb-1 block">
              Portfolio Images ({form.portfolio_images.length})
            </label>
            {form.portfolio_images.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-3">
                {form.portfolio_images.map((url, idx) => (
                  <div key={idx} className="relative group">
                    <img src={url} alt={`Portfolio ${idx + 1}`} className="w-full h-24 object-cover" />
                    <button
                      type="button"
                      onClick={() => removePortfolioImage(idx)}
                      data-testid={`artist-portfolio-remove-${idx}`}
                      className="absolute top-1 right-1 bg-red-500 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <ImageUploadField
              value={newPortfolioUrl}
              onChange={(url) => {
                if (url) addPortfolioImage(url);
              }}
              label="Add portfolio image"
              testId="artist-portfolio-add"
            />
            <p className="font-mono text-xs text-gray-500 mt-1">
              Upload or paste an image URL. It will be added to the list above.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ArtistsManager;
